import React, { useEffect, useState } from 'react';

/*
  Enterprise-grade splash screen for StayNivo HMS.

  Design principles:
  - Uses the actual PNG logo icon (no SVG recreation) — pixel-perfect every time
  - Clean sequential reveals: icon → brand → tagline → dots
  - Smooth ease-out curves only, no spring / bounce / elastic effects
  - Thin animated accent lines for a premium, modern feel
  - Consistent with a professional B2B SaaS product
*/

const CSS = `
.spl {
  position: fixed; inset: 0; z-index: 9999;
  background: #050b14;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  overflow: hidden;
}
.spl.out {
  transition: opacity 0.6s ease-in;
  opacity: 0;
  pointer-events: none;
}

/* ── background gradient ── */
@keyframes bg-reveal {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* ── logo: fade + very subtle upward drift ── */
@keyframes logo-in {
  from { opacity: 0; transform: translateY(12px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0)   scale(1); }
}

/* ── icon container: continuous breathe zoom + glow after entrance ── */
@keyframes icon-breathe {
  0%   { transform: scale(1);    box-shadow: 0 0 0 4px rgba(28,201,232,0.07), 0 10px 40px rgba(0,140,200,0.32); }
  50%  { transform: scale(1.04); box-shadow: 0 0 0 6px rgba(28,201,232,0.14), 0 14px 52px rgba(0,180,230,0.48); }
  100% { transform: scale(1);    box-shadow: 0 0 0 4px rgba(28,201,232,0.07), 0 10px 40px rgba(0,140,200,0.32); }
}

/* ── brand text ── */
@keyframes brand-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── tagline ── */
@keyframes tag-in {
  from { opacity: 0; }
  to   { opacity: 0.55; }
}

/* ── accent line that expands from center ── */
@keyframes line-in {
  from { transform: scaleX(0); opacity: 0; }
  to   { transform: scaleX(1); opacity: 1; }
}

/* ── loading dots ── */
@keyframes dot-pulse {
  0%, 100% { opacity: 0.25; transform: scale(0.8); }
  50%       { opacity: 1;    transform: scale(1.2); }
}

/* ── progress bar ── */
@keyframes prog-fill {
  from { width: 0%; }
  to   { width: 100%; }
}
@keyframes shimmer {
  from { background-position: -200% center; }
  to   { background-position:  200% center; }
}
`;

export default function SplashScreen({ leaving }) {
  const [go, setGo] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setGo(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const ease = 'cubic-bezier(0.25, 0, 0.15, 1)';

  const anim = (name, dur, delay, extra = '') =>
    go ? `${name} ${dur}ms ${ease} ${delay}ms both${extra ? ` ${extra}` : ''}` : 'none';

  return (
    <>
      <style>{CSS}</style>
      <div className={`spl${leaving ? ' out' : ''}`}>

        {/* Radial ambient glow — stationary, no animation distraction */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 60% 50% at 30% 40%, rgba(20,140,210,0.08) 0%, transparent 70%),' +
            'radial-gradient(ellipse 50% 40% at 75% 65%, rgba(0,200,230,0.06) 0%, transparent 70%)',
        }}/>

        {/* ── Content column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, zIndex: 1 }}>

          {/*
            Icon container — two-div structure so the border stays crisp while
            the image can have a gradient mask hiding the "Staynivo" text below.

            Geometry (scale = 178/384 = 0.464):
              cloud top  in container = 13 + 9.3  = 22.3 px  (top margin)
              cloud btm  in container = 13 + 104.4 = 117.4 px (bottom margin = 22.6 px)
              cloud center = 69.85 px ≈ container center 70 px  ← centred ✓
              text starts at container y = 126.7 px → inside mask fade zone ✓
            After zoom scale(1.04) all margins grow ×1.04 — no edge touching ✓
          */}
          <div style={{
            marginBottom: 24,
            animation: anim('logo-in', 550, 80),
            opacity: go ? undefined : 0,
          }}>
            {/* Outer: border + shadow + breathe animation */}
            <div style={{
              position: 'relative',
              width: 140,
              height: 140,
              borderRadius: 28,
              overflow: 'hidden',
              border: '1.5px solid rgba(28,201,232,0.38)',
              boxShadow: '0 0 0 4px rgba(28,201,232,0.07), 0 10px 40px rgba(0,140,200,0.32)',
              animation: go ? 'icon-breathe 2.8s ease-in-out 630ms infinite' : 'none',
            }}>
              {/* Inner: image + gradient mask (border above is unaffected by mask).
                  background-position-y: -2px shifts the image 15 px higher than the
                  previous +13 px offset, making the top gap ≈7 px and the bottom gap
                  (before the mask fade) ≈5 px — visually balanced. */}
              <div style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: '#090d1b',
                backgroundImage: 'url(/icons/icon-384x384.png)',
                backgroundSize: '178px 178px',
                backgroundPosition: 'center -2px',
                backgroundRepeat: 'no-repeat',
                WebkitMaskImage: 'linear-gradient(to bottom, black 77%, transparent 84%)',
                maskImage: 'linear-gradient(to bottom, black 77%, transparent 84%)',
              }} />
            </div>
          </div>

          {/* Brand name */}
          <div style={{
            animation: anim('brand-in', 420, 340),
            opacity: go ? undefined : 0,
            marginBottom: 10,
          }}>
            <span style={{
              fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
              fontSize: 30,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              lineHeight: 1,
            }}>
              <span style={{ color: '#ffffff' }}>Stay</span>
              <span style={{ color: '#1cc9e8' }}>nivo</span>
            </span>
          </div>

          {/* Accent line — expands from center, adds premium feel */}
          <div style={{
            width: 160,
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(28,201,232,0.5), transparent)',
            transformOrigin: 'center',
            animation: anim('line-in', 400, 460),
            opacity: go ? undefined : 0,
            marginBottom: 12,
          }}/>

          {/* Tagline */}
          <div style={{
            animation: anim('tag-in', 400, 540),
            opacity: go ? undefined : 0,
            marginBottom: 28,
          }}>
            <span style={{
              fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.22em',
              color: 'rgba(200,235,255,0.85)',
            }}>
              POWERING THE FUTURE OF HOSPITALITY
            </span>
          </div>

          {/* Loading dots */}
          <div style={{
            display: 'flex', gap: 8,
            animation: anim('tag-in', 350, 700),
            opacity: go ? undefined : 0,
          }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 5, height: 5, borderRadius: '50%',
                background: i === 1 ? '#1cc9e8' : 'rgba(28,201,232,0.4)',
                animation: go
                  ? `dot-pulse 1.2s ease-in-out ${i * 200}ms infinite`
                  : 'none',
              }}/>
            ))}
          </div>
        </div>

        {/* Progress bar at bottom */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 2, background: 'rgba(255,255,255,0.04)',
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #0ea5c8 0%, #1cc9e8 50%, #0ea5c8 100%)',
            backgroundSize: '200% 100%',
            animation: go
              ? `prog-fill 2.4s cubic-bezier(0.4,0,0.2,1) 80ms both, shimmer 1.6s linear 80ms infinite`
              : 'none',
          }}/>
        </div>

      </div>
    </>
  );
}
